import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useLocale } from '../../contexts/LocaleContext';
import { LLMModelSelector } from './LLMModelSelector';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { DatePicker } from '../DatePicker';
import { collectUniqueFilterNames } from '../../utils/collect-unique-filter-names';
import {
  buildTypeColorMap,
  collectOrderedChartTypes,
  computeCallCostPerCredit,
  computeCostPer1kCredits,
  getCostEfficiencyColor,
  getCostEfficiencyLevel,
} from '../../utils/llm-admin-charts';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_BADGE_INFO_CLASSNAME,
  ADMIN_BADGE_PURPLE_CLASSNAME,
  ADMIN_BADGE_SUCCESS_CLASSNAME,
  ADMIN_CHARTS_GRID_CLASSNAME,
  ADMIN_FILTER_BAR_CLASSNAME,
  ADMIN_INLINE_SELECT_CLASSNAME,
  ADMIN_MODAL_CLASSNAME,
  ADMIN_MODAL_OVERLAY_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  ADMIN_PANEL_CLASSNAME,
  ADMIN_PANEL_TITLE_CLASSNAME,
  ADMIN_STAT_CARD_CLASSNAME,
  ADMIN_STAT_LABEL_CLASSNAME,
  ADMIN_STAT_VALUE_CLASSNAME,
  ADMIN_STATS_ROW_CLASSNAME,
  ADMIN_TABLE_CELL_MUTED_CLASSNAME,
  ADMIN_TABLE_LINK_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminTableWrap,
} from './admin-ui';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

interface LlmTypeChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
  }>;
  typeColorMap: Record<string, string>;
  valueLabel: string;
  formatValue: (value: number) => string;
}

const LlmTypeChartTooltip: React.FC<LlmTypeChartTooltipProps> = ({
  active,
  payload,
  typeColorMap,
  valueLabel,
  formatValue,
}) => {
  if (!active || !payload?.length) {
    return null;
  }

  const entry = payload[0];
  const typeName = entry.name ?? '';
  const color = typeColorMap[typeName] ?? entry.color ?? '#6b7280';
  const value = typeof entry.value === 'number' ? entry.value : 0;

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3 text-foreground shadow-md">
      <div className="mb-2 flex items-center gap-2">
        <span
          className="h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-sm font-semibold text-foreground">{typeName}</span>
      </div>
      <div className="text-sm text-muted-foreground">
        {valueLabel}: {formatValue(value)}
      </div>
    </div>
  );
};



interface FileAttachment {
  fileName: string;
  mimeType: string;
  size: number;
  fileId: string | null;
  s3Key: string | null;
}

interface LLMCallOptions {
  webSearch?: boolean;
}

interface LLMCall {
  id: string;
  userId: string | null;
  userEmail?: string;
  userAdmin?: boolean;
  documentId: string | null;
  documentTitle?: string;
  triggerSource: string;
  provider: 'gemini' | 'openai';
  model: string;
  prompt: string;
  response: string | null;
  promptTokens: number | null;
  responseTokens: number | null;
  costUsd: number | null;
  creditsUsed: number | null;
  durationSeconds: number | null;
  fileAttachments: FileAttachment[];
  wordCount: number | null;
  options?: LLMCallOptions | null;
  createdAt: string;
}

export const LLMCalls: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [calls, setCalls] = useState<LLMCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCall, setSelectedCallState] = useState<LLMCall | null>(null);
  const [styleNotesRegenerationLog, setStyleNotesRegenerationLog] = useState<{
    id: string;
    outcome: string;
    calibrationScore: number;
    threshold: number;
    scoreBreakdown: {
      rejections: number;
      acceptances: number;
      reports: number;
      styleNotesActiveBonus: number;
    };
    documentId: string;
  } | null>(null);

  const updateUrlCallId = useCallback((callId: string | null): void => {
    const urlParams = new URLSearchParams(window.location.search);
    if (callId) {
      urlParams.set('callId', callId);
    } else {
      urlParams.delete('callId');
    }
    const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
  }, []);

  const setSelectedCall = useCallback((call: LLMCall | null): void => {
    setSelectedCallState(call);
    updateUrlCallId(call?.id ?? null);
  }, [updateUrlCallId]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedTriggerSource, setSelectedTriggerSource] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [excludeAdmin, setExcludeAdmin] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCalls, setTotalCalls] = useState<number>(0);
  const [aggregations, setAggregations] = useState<{
    totalCalls: number;
    totalCost: number;
    totalCreditsUsed: number;
    costPerCredit: number | null;
    geminiCost: number;
    openaiCost: number;
    geminiCount: number;
    openaiCount: number;
    inputTokens: number;
    outputTokens: number;
    callsByType?: Array<{ name: string; value: number }>;
    costByType?: Array<{ name: string; value: number }>;
  } | null>(null);
  const [globalLlmModel, setGlobalLlmModel] = useState<string>('');
  const [savedGlobalLlmModel, setSavedGlobalLlmModel] = useState<string>('');
  const [savingGlobalModel, setSavingGlobalModel] = useState(false);
  const [fileDownloadUrls, setFileDownloadUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    setCurrentPage(1);
  }, [excludeAdmin, selectedUserId, selectedTriggerSource, startDate, endDate]);

  useEffect(() => {
    const fetchCalls = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: currentPage.toString(),
          pageSize: '25',
          excludeAdmin: excludeAdmin.toString(),
        });
        if (selectedUserId) {
          params.append('userId', selectedUserId);
        }
        if (selectedTriggerSource) {
          params.append('triggerSource', selectedTriggerSource);
        }
        if (startDate) {
          params.append('startDate', startDate);
        }
        if (endDate) {
          params.append('endDate', endDate);
        }
        const url = masterKey
          ? `${API_BASE}/api/llm-calls?${params.toString()}&masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/llm-calls?${params.toString()}`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch LLM calls');
        }

        const data = await response.json();
        setCalls(data.calls || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalCalls(data.pagination?.total || 0);
        setAggregations(data.aggregations || null);
        setError(null);

        const urlParams = new URLSearchParams(window.location.search);
        const callId = urlParams.get('callId');
        if (callId && data.calls) {
          const call = data.calls.find((c: LLMCall) => c.id === callId);
          if (call) {
            setSelectedCallState(call);
          }
        }
      } catch (err) {
        console.error('Error fetching LLM calls:', err);
        setError('Error al cargar llamadas LLM');
      } finally {
        setLoading(false);
      }
    };

    fetchCalls();
  }, [token, user, masterKey, currentPage, excludeAdmin, selectedUserId, selectedTriggerSource, startDate, endDate]);

  useEffect(() => {
    const fetchGlobalLlmSettings = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        return;
      }

      try {
        const url = masterKey
          ? `${API_BASE}/api/admin-settings/llm?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/admin-settings/llm`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setGlobalLlmModel(data.globalLlmModel || '');
          setSavedGlobalLlmModel(data.globalLlmModel || '');
        }
      } catch (err) {
        console.error('Error fetching global LLM settings:', err);
      }
    };

    fetchGlobalLlmSettings();
  }, [token, user, masterKey]);

  useEffect(() => {
    const fetchStyleNotesRegenerationLog = async () => {
      if (!selectedCall || selectedCall.triggerSource !== 'style_notes' || (!token && !masterKey)) {
        setStyleNotesRegenerationLog(null);
        return;
      }

      try {
        const url = masterKey
          ? `${API_BASE}/api/admin/style-notes/llm-calls/${selectedCall.id}/regeneration-log?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/admin/style-notes/llm-calls/${selectedCall.id}/regeneration-log`;
        const response = await fetch(url, {
          headers: token && !masterKey ? { Authorization: `Bearer ${token}` } : {},
        });

        if (response.ok) {
          const data = await response.json();
          setStyleNotesRegenerationLog(data.entry);
        } else {
          setStyleNotesRegenerationLog(null);
        }
      } catch (err) {
        console.error('Error fetching style notes regeneration log:', err);
        setStyleNotesRegenerationLog(null);
      }
    };

    void fetchStyleNotesRegenerationLog();
  }, [masterKey, selectedCall, token]);

  const handleSaveGlobalModel = async () => {
    if ((!token && !masterKey)) return;

    setSavingGlobalModel(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/admin-settings/llm?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/admin-settings/llm`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ globalLlmModel }),
      });

      if (response.ok) {
        const data = await response.json();
        setSavedGlobalLlmModel(data.globalLlmModel || '');
      }
    } catch (err) {
      console.error('Error saving global LLM model:', err);
    } finally {
      setSavingGlobalModel(false);
    }
  };

  const uniqueUsers = React.useMemo(() => {
    const usersMap = new Map<string, string>();
    calls.forEach((call) => {
      if (call.userId && call.userEmail) {
        usersMap.set(call.userId, call.userEmail);
      }
    });
    return Array.from(usersMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [calls]);

  const uniqueTriggerSources = React.useMemo(() => {
    return collectUniqueFilterNames(
      aggregations?.callsByType,
      calls.map((call) => call.triggerSource)
    );
  }, [aggregations, calls]);

  const callsByTypeData = useMemo(() => {
    return aggregations?.callsByType || [];
  }, [aggregations]);

  const costByTypeData = useMemo(() => {
    return aggregations?.costByType || [];
  }, [aggregations]);

  const chartTypes = useMemo(() => {
    return collectOrderedChartTypes(callsByTypeData, costByTypeData);
  }, [callsByTypeData, costByTypeData]);

  const typeColorMap = useMemo(() => {
    return buildTypeColorMap(chartTypes);
  }, [chartTypes]);

  const averageCostPerCredit = aggregations?.costPerCredit ?? null;
  const averageCostPer1kCredits = computeCostPer1kCredits(averageCostPerCredit);

  const getCallCostColor = useCallback((call: LLMCall): string => {
    const callCostPerCredit = computeCallCostPerCredit(call.costUsd, call.creditsUsed);
    return getCostEfficiencyColor(getCostEfficiencyLevel(callCostPerCredit, averageCostPerCredit));
  }, [averageCostPerCredit]);

  const getCurrentIndex = useCallback((): number => {
    if (!selectedCall) return -1;
    return calls.findIndex(call => call.id === selectedCall.id);
  }, [selectedCall, calls]);

  const navigateToCall = useCallback((direction: 'prev' | 'next'): void => {
    const currentIndex = getCurrentIndex();
    
    if (currentIndex === -1) return;

    let targetIndex: number;
    if (direction === 'prev') {
      targetIndex = currentIndex - 1;
    } else {
      targetIndex = currentIndex + 1;
    }

    if (targetIndex < 0 || targetIndex >= calls.length) return;

    setSelectedCall(calls[targetIndex]);
  }, [getCurrentIndex, calls]);

  const currentIndex = getCurrentIndex();
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < calls.length - 1;

  useEffect(() => {
    if (!selectedCall) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        navigateToCall('prev');
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        navigateToCall('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedCall, hasPrevious, hasNext, navigateToCall]);

  useEffect(() => {
    if (!selectedCall || !selectedCall.fileAttachments || selectedCall.fileAttachments.length === 0) {
      setFileDownloadUrls({});
      return;
    }

    const fetchDownloadUrls = async () => {
      const urls: Record<string, string> = {};
      for (let i = 0; i < selectedCall.fileAttachments.length; i++) {
        const attachment = selectedCall.fileAttachments[i];
        if (attachment.s3Key) {
          try {
            const url = masterKey
              ? `${API_BASE}/api/llm-calls/${selectedCall.id}/file-attachment/${i}/download-url?masterKey=${encodeURIComponent(masterKey)}`
              : `${API_BASE}/api/llm-calls/${selectedCall.id}/file-attachment/${i}/download-url`;
            const response = await fetch(url, {
              headers: {
                ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
              },
            });
            if (response.ok) {
              const data = await response.json();
              urls[`${selectedCall.id}-${i}`] = data.downloadUrl;
            }
          } catch (error) {
            console.error(`Error fetching download URL for attachment ${i}:`, error);
          }
        }
      }
      setFileDownloadUrls(urls);
    };

    fetchDownloadUrls();
  }, [selectedCall, token, masterKey]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  const inputTokens = aggregations?.inputTokens || 0;
  const outputTokens = aggregations?.outputTokens || 0;

  const totalCost = aggregations?.totalCost || 0;
  const totalCreditsUsed = aggregations?.totalCreditsUsed || 0;
  const geminiCost = aggregations?.geminiCost || 0;
  const openaiCost = aggregations?.openaiCost || 0;

  const geminiCount = aggregations?.geminiCount || 0;
  const openaiCount = aggregations?.openaiCount || 0;

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        <div className={ADMIN_FILTER_BAR_CLASSNAME}>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <label htmlFor="user-filter" className={ADMIN_MUTED_TEXT_CLASSNAME}>
                {t('filter_by_user')}:
              </label>
              <select
                id="user-filter"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className={ADMIN_INLINE_SELECT_CLASSNAME}
              >
                <option value="">{t('all_users')}</option>
                {uniqueUsers.map(([userId, userEmail]) => (
                  <option key={userId} value={userId}>
                    {userEmail}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="type-filter" className={ADMIN_MUTED_TEXT_CLASSNAME}>
                {t('filter_by_type')}:
              </label>
              <select
                id="type-filter"
                value={selectedTriggerSource}
                onChange={(e) => setSelectedTriggerSource(e.target.value)}
                className={ADMIN_INLINE_SELECT_CLASSNAME}
              >
                <option value="">{t('all_types')}</option>
                {uniqueTriggerSources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="start-date-filter" className={ADMIN_MUTED_TEXT_CLASSNAME}>
                Fecha inicio:
              </label>
              <DatePicker
                id="start-date-filter"
                value={startDate}
                onChange={setStartDate}
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="end-date-filter" className={ADMIN_MUTED_TEXT_CLASSNAME}>
                Fecha fin:
              </label>
              <DatePicker
                id="end-date-filter"
                value={endDate}
                onChange={setEndDate}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="exclude-admin-filter"
                checked={excludeAdmin}
                onCheckedChange={(checked) => setExcludeAdmin(checked === true)}
              />
              <Label htmlFor="exclude-admin-filter" className="cursor-pointer text-sm font-normal text-muted-foreground">
                Excluir admin
              </Label>
            </div>
          </div>
        </div>

        <div className={ADMIN_PANEL_CLASSNAME}>
          <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-3 font-medium`}>
            Enlaces de facturación y uso
          </div>
          <div className="flex flex-wrap gap-4">
            <Button variant="secondary" size="sm" asChild>
              <a
                href="https://aistudio.google.com/usage"
                target="_blank"
                rel="noopener noreferrer"
              >
                Gemini - Uso y facturación
              </a>
            </Button>
            <Button
              size="sm"
              asChild
              className="bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
            >
              <a
                href="https://platform.openai.com/usage"
                target="_blank"
                rel="noopener noreferrer"
              >
                OpenAI - Uso y facturación
              </a>
            </Button>
          </div>
        </div>

        <div className={ADMIN_PANEL_CLASSNAME}>
          <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-3 font-medium`}>
            Modelo LLM Global
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <LLMModelSelector
              value={globalLlmModel}
              onChange={setGlobalLlmModel}
              emptyLabel="Sin fijar (usar reglas normales)"
              className="w-full min-w-0 sm:min-w-[250px] sm:flex-1"
            />
            <Button
              onClick={handleSaveGlobalModel}
              disabled={savingGlobalModel || globalLlmModel === savedGlobalLlmModel}
            >
              {savingGlobalModel ? 'Guardando...' : 'Guardar'}
            </Button>
            {savedGlobalLlmModel && (
              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                ✓ Modelo global activo
              </span>
            )}
          </div>
          <p className={`${ADMIN_MUTED_TEXT_CLASSNAME} mt-2 text-xs`}>
            Si se establece, se usará como modelo por defecto cuando el usuario no tenga un modelo fijo. Los modelos fijos de usuario tienen prioridad.
          </p>
        </div>

        {error && (
          <div className="mb-8">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        <div className={`${ADMIN_STATS_ROW_CLASSNAME} flex-col`}>
          <div className="flex w-full flex-wrap gap-4">
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>Total</div>
              <div className={ADMIN_STAT_VALUE_CLASSNAME}>{aggregations?.totalCalls || 0}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>OpenAI</div>
              <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-emerald-600 dark:text-emerald-400`}>{openaiCount}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>Gemini</div>
              <div className={ADMIN_STAT_VALUE_CLASSNAME}>{geminiCount}</div>
            </div>
          </div>
          <div className="flex w-full flex-wrap gap-4">
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>Input tokens</div>
              <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-amber-600 dark:text-amber-400`}>{inputTokens.toLocaleString()}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>Output tokens</div>
              <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-amber-600 dark:text-amber-400`}>{outputTokens.toLocaleString()}</div>
            </div>
          </div>
          <div className="flex w-full flex-wrap gap-4">
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>Costo Total</div>
              <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-emerald-600 dark:text-emerald-400`}>${totalCost.toFixed(6)}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>{t('credits_used')}</div>
              <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-red-600 dark:text-red-400`}>{totalCreditsUsed.toLocaleString()}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>Costo OpenAI</div>
              <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-emerald-600 dark:text-emerald-400`}>${openaiCost.toFixed(6)}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>Costo Gemini</div>
              <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-emerald-600 dark:text-emerald-400`}>${geminiCost.toFixed(6)}</div>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className={`${ADMIN_STAT_CARD_CLASSNAME} mb-4 max-w-[320px]`}>
            <div className={ADMIN_STAT_LABEL_CLASSNAME}>
              {t('llm_cost_per_1k_credits')}
            </div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>
              {averageCostPer1kCredits !== null ? `$${averageCostPer1kCredits.toFixed(4)}` : '-'}
            </div>
          </div>

          <div className={ADMIN_CHARTS_GRID_CLASSNAME}>
            <div className={ADMIN_PANEL_CLASSNAME}>
              <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
                {t('llm_calls_by_type')}
              </h2>
              {callsByTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={callsByTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ percent }) => `${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      innerRadius={40}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                    >
                      {callsByTypeData.map((entry) => (
                        <Cell key={`calls-${entry.name}`} fill={typeColorMap[entry.name]} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={(
                        <LlmTypeChartTooltip
                          typeColorMap={typeColorMap}
                          valueLabel="Llamadas"
                          formatValue={(value) => value.toLocaleString()}
                        />
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  No hay datos
                </div>
              )}
            </div>

            <div className={ADMIN_PANEL_CLASSNAME}>
              <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
                {t('llm_cost_by_type')}
              </h2>
              {costByTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={costByTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ percent }) => `${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      innerRadius={40}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                    >
                      {costByTypeData.map((entry) => (
                        <Cell key={`cost-${entry.name}`} fill={typeColorMap[entry.name]} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={(
                        <LlmTypeChartTooltip
                          typeColorMap={typeColorMap}
                          valueLabel="Costo"
                          formatValue={(value) => `$${value.toFixed(6)}`}
                        />
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  No hay datos
                </div>
              )}
            </div>
          </div>

          {chartTypes.length > 0 && (
            <div className={`${ADMIN_PANEL_CLASSNAME} mt-4`}>
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-3 font-medium`}>
                {t('llm_chart_legend')}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {chartTypes.map((type) => (
                  <div key={type} className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: typeColorMap[type] }}
                    />
                    <span className="text-sm text-foreground">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {calls.length === 0 ? (
          <AdminEmptyState>No hay llamadas LLM registradas</AdminEmptyState>
        ) : (
          <>
          <AdminTableWrap>
              <table className="admin-data-table min-w-[1200px]">
                <thead>
                  <tr>
                    <th>
                      Fecha
                    </th>
                    <th>
                      Usuario
                    </th>
                    <th>
                      Origen
                    </th>
                    <th>
                      Proveedor
                    </th>
                    <th>
                      Modelo
                    </th>
                    <th>
                      Tokens
                    </th>
                    <th>
                      Palabras
                    </th>
                    <th>
                      Duración
                    </th>
                    <th>
                      Costo
                    </th>
                    <th>
                      {t('credits_used')}
                    </th>
                    <th>
                      Estado
                    </th>
                    <th>
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <tr
                      key={call.id}
                    >
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {new Date(call.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td>
                        {call.userId && call.userEmail ? (
                          <Link
                            to={`/admin/users/${call.userId}`}
                            className={ADMIN_TABLE_LINK_CLASSNAME}
                          >
                            {call.userEmail}
                          </Link>
                        ) : (
                          call.userEmail || '-'
                        )}
                      </td>
                      <td>
                        <span className={ADMIN_BADGE_PURPLE_CLASSNAME}>
                          {call.triggerSource}
                        </span>
                      </td>
                      <td>
                        <span
                          className={
                            call.provider === 'gemini'
                              ? ADMIN_BADGE_INFO_CLASSNAME
                              : ADMIN_BADGE_SUCCESS_CLASSNAME
                          }
                        >
                          {call.provider}
                        </span>
                      </td>
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {call.model}
                      </td>
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {((call.promptTokens || 0) + (call.responseTokens || 0)).toLocaleString()}
                      </td>
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {call.wordCount !== null ? call.wordCount.toLocaleString() : '-'}
                      </td>
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {call.durationSeconds !== null ? `${call.durationSeconds.toFixed(2)}s` : '-'}
                      </td>
                      <td className="font-medium" style={{ color: getCallCostColor(call) }}>
                        {call.costUsd !== null && call.costUsd > 0 ? `$${call.costUsd.toFixed(6)}` : '-'}
                      </td>
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {call.creditsUsed !== null && call.creditsUsed > 0 ? call.creditsUsed.toLocaleString() : '-'}
                      </td>
                      <td className="text-center">
                        {call.response === null ? (
                          <span title={t('pending')}>⏳</span>
                        ) : call.response.startsWith('Error:') ? (
                          <span title={t('error')}>❌</span>
                        ) : (
                          <span title={t('success_status')}>✅</span>
                        )}
                      </td>
                      <td>
                        <Button size="sm" onClick={() => setSelectedCall(call)}>
                          Ver Detalles
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableWrap>
            {totalPages > 1 && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 border-t border-border pt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  Primera
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span className="px-3 text-sm text-muted-foreground">
                  Página {currentPage} de {totalPages} ({totalCalls} total)
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Siguiente
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages}
                >
                  Última
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedCall && (
        <div
          className={ADMIN_MODAL_OVERLAY_CLASSNAME}
          onClick={() => setSelectedCall(null)}
        >
          <div
            className={`${ADMIN_MODAL_CLASSNAME} max-w-[900px]`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {hasPrevious && (
                  <Button size="sm" onClick={() => navigateToCall('prev')}>
                    ← Anterior
                  </Button>
                )}
                {hasNext && (
                  <Button size="sm" onClick={() => navigateToCall('next')}>
                    Siguiente →
                  </Button>
                )}
              </div>
              <h2 className="m-0 text-lg font-semibold text-foreground">Detalles de la Llamada LLM</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedCall(null)}
                className="text-xl leading-none"
              >
                ×
              </Button>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Fecha</div>
              <div className="text-base text-foreground">
                {new Date(selectedCall.createdAt).toLocaleString('es-ES')}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Usuario</div>
              <div className="text-base text-foreground">
                {selectedCall.userId && selectedCall.userEmail ? (
                  <Link
                    to={`/admin/users/${selectedCall.userId}`}
                    className={ADMIN_TABLE_LINK_CLASSNAME}
                  >
                    {selectedCall.userEmail}
                  </Link>
                ) : (
                  selectedCall.userEmail || 'N/A'
                )}
              </div>
            </div>

            <div className="mb-6 grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 [&>div]:min-w-0">
              <div>
                <div className="mb-1 text-xs text-muted-foreground">Usuario admin</div>
                <div className="text-sm text-foreground">{selectedCall.userAdmin ? 'Sí' : 'No'}</div>
              </div>
              <div>
                <div className="mb-1 text-xs text-muted-foreground">Título de documento</div>
                <div className="text-sm text-foreground">{selectedCall.documentTitle || 'N/A'}</div>
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Origen</div>
              <div className="text-base text-foreground">{selectedCall.triggerSource}</div>
            </div>

            {selectedCall.triggerSource === 'style_notes' && styleNotesRegenerationLog && (
              <div className="mb-6 rounded-md bg-muted/40 p-3">
                <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>
                  {t('admin_style_notes_regeneration_context')}
                </div>
                <div className="text-sm text-foreground">
                  {t(`admin_style_notes_outcome_${styleNotesRegenerationLog.outcome}`)} · {styleNotesRegenerationLog.calibrationScore}/{styleNotesRegenerationLog.threshold}
                </div>
                <div className="mt-1 text-[0.8125rem] text-muted-foreground">
                  {t('admin_style_notes_breakdown_rejections')}: {styleNotesRegenerationLog.scoreBreakdown.rejections} · {t('admin_style_notes_breakdown_acceptances')}: {styleNotesRegenerationLog.scoreBreakdown.acceptances} · {t('admin_style_notes_breakdown_reports')}: {styleNotesRegenerationLog.scoreBreakdown.reports}
                </div>
                {selectedCall.documentId && (
                  <div className="mt-2">
                    <Link
                      to={`/admin/users/${selectedCall.userId}`}
                      className={`${ADMIN_TABLE_LINK_CLASSNAME} text-[0.8125rem]`}
                    >
                      {t('admin_style_notes_view_document_calibration')}
                    </Link>
                  </div>
                )}
              </div>
            )}

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Proveedor / Modelo</div>
              <div className="text-base text-foreground">
                {selectedCall.provider} / {selectedCall.model}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Tokens</div>
              <div className="text-base text-foreground">
                Prompt: {selectedCall.promptTokens?.toLocaleString() || 'N/A'} | Respuesta:{' '}
                {selectedCall.responseTokens?.toLocaleString() || 'N/A'} | Total:{' '}
                {((selectedCall.promptTokens || 0) + (selectedCall.responseTokens || 0)).toLocaleString()}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Palabras</div>
              <div className="text-base text-foreground">
                {selectedCall.wordCount !== null ? selectedCall.wordCount.toLocaleString() : 'N/A'}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Opciones</div>
              <div className="text-base text-foreground">
                <div>
                  <strong>Búsqueda web:</strong>{' '}
                  {selectedCall.options && typeof selectedCall.options.webSearch !== 'undefined'
                    ? selectedCall.options.webSearch
                      ? 'Sí'
                      : 'No'
                    : 'N/A'}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Duración</div>
              <div className="text-base text-foreground">
                {selectedCall.durationSeconds !== null ? `${selectedCall.durationSeconds.toFixed(2)} segundos` : 'N/A'}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Costo</div>
              <div className="text-base font-medium" style={{ color: getCallCostColor(selectedCall) }}>
                {selectedCall.costUsd !== null && selectedCall.costUsd > 0
                  ? `$${selectedCall.costUsd.toFixed(6)}`
                  : 'N/A'}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('credits_used')}</div>
              <div className="text-base text-foreground">
                {selectedCall.creditsUsed !== null && selectedCall.creditsUsed > 0
                  ? selectedCall.creditsUsed.toLocaleString()
                  : 'N/A'}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Estado</div>
              <div className="flex items-center gap-2 text-base text-foreground">
                {selectedCall.response === null ? (
                  <>
                    <span>⏳</span>
                    <span>Pendiente</span>
                  </>
                ) : selectedCall.response.startsWith('Error:') ? (
                  <>
                    <span>❌</span>
                    <span className="text-red-600 dark:text-red-400">Error</span>
                  </>
                ) : (
                  <>
                    <span>✅</span>
                    <span className="text-emerald-600 dark:text-emerald-400">Éxito</span>
                  </>
                )}
              </div>
            </div>

            {(() => {
              const promptParts = selectedCall.prompt.split('\n\n---\n\n');
              const hasSystemUser = promptParts.length === 2;

              if (hasSystemUser) {
                return (
                  <>
                    <div className="mb-6">
                      <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>
                        System Prompt
                      </div>
                      <div className="max-h-[200px] overflow-auto whitespace-pre-wrap rounded-md border border-amber-300 bg-amber-50 p-4 font-mono text-sm text-foreground dark:border-amber-800 dark:bg-amber-950/40">
                        {promptParts[0]}
                      </div>
                    </div>
                    <div className="mb-6">
                      <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>
                        User Prompt
                      </div>
                      <div className="max-h-[200px] overflow-auto whitespace-pre-wrap rounded-md border border-sky-300 bg-sky-50 p-4 font-mono text-sm text-foreground dark:border-sky-800 dark:bg-sky-950/40">
                        {promptParts[1]}
                      </div>
                    </div>
                  </>
                );
              }

              return (
                <div className="mb-6">
                  <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Prompt</div>
                  <div className="max-h-[200px] overflow-auto whitespace-pre-wrap rounded-md bg-muted p-4 font-mono text-sm text-foreground">
                    {selectedCall.prompt}
                  </div>
                </div>
              );
            })()}

            {selectedCall.fileAttachments && selectedCall.fileAttachments.length > 0 && (
              <div className="mb-6">
                <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>
                  {selectedCall.fileAttachments.length === 1 ? 'Archivo Adjunto' : `Archivos Adjuntos (${selectedCall.fileAttachments.length})`}
                </div>
                {selectedCall.fileAttachments.map((attachment, index) => (
                  <div
                    key={index}
                    className={`rounded-md border border-sky-200 bg-sky-50 p-4 text-sm text-foreground dark:border-sky-800 dark:bg-sky-950/40 ${
                      index < selectedCall.fileAttachments.length - 1 ? 'mb-3' : ''
                    }`}
                  >
                    <div className="mb-2">
                      <strong>Nombre:</strong> {attachment.fileName}
                    </div>
                    <div className="mb-2">
                      <strong>Tipo MIME:</strong> {attachment.mimeType}
                    </div>
                    <div className="mb-2">
                      <strong>Tamaño:</strong> {formatFileSize(attachment.size)}
                    </div>
                    {attachment.fileId && (
                      <div className="mb-2">
                        <strong>ID de archivo:</strong> {attachment.fileId}
                      </div>
                    )}
                    {fileDownloadUrls[`${selectedCall.id}-${index}`] && (
                      <div className="mt-3">
                        <Button size="sm" asChild>
                          <a
                            href={fileDownloadUrls[`${selectedCall.id}-${index}`]}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Descargar desde S3
                          </a>
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Respuesta</div>
              <div className="max-h-[400px] overflow-auto whitespace-pre-wrap rounded-md bg-muted p-4 font-mono text-sm text-foreground">
                {selectedCall.response || 'Sin respuesta'}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
